import { useState, useEffect } from "react";
import { adminApi } from "@/lib/api";
import { Mail, Phone, Calendar } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await adminApi.get(`/admin/inquiries`);
      setInquiries(response.data);
    } catch (error) {
      console.error("Error fetching inquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (inquiryId) => {
    try {
      await adminApi.put(`/admin/inquiries/${inquiryId}/status?status=responded`);
      toast.success("Inquiry marked as responded");
      fetchInquiries();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-inquiries">
        <h1 className="text-3xl font-serif mb-8">Manage Inquiries</h1>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : inquiries.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center">
            <p className="text-muted-foreground">No inquiries yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {inquiries.map((inquiry) => (
              <div key={inquiry.id} className="bg-white rounded-lg p-6 shadow-sm" data-testid={`inquiry-${inquiry.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">{inquiry.name}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center">
                        <Mail size={16} className="mr-1" />
                        {inquiry.email}
                      </span>
                      <span className="flex items-center">
                        <Phone size={16} className="mr-1" />
                        {inquiry.phone}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs ${
                      inquiry.status === "responded"
                        ? "bg-green-100 text-green-700"
                        : "bg-orange-100 text-orange-700"
                    }`}>
                      {inquiry.status}
                    </span>
                  </div>
                </div>

                <div className="bg-muted p-4 rounded-lg mb-4">
                  <p className="text-muted-foreground">{inquiry.message}</p>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center">
                    <Calendar size={16} className="mr-1" />
                    {new Date(inquiry.created_at).toLocaleDateString()}
                  </span>
                  {inquiry.status === "new" && (
                    <Button
                      onClick={() => handleStatusUpdate(inquiry.id)}
                      size="sm"
                      data-testid={`mark-responded-${inquiry.id}`}
                    >
                      Mark as Responded
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminInquiries;
